import { excludeEmptyValues, formatAccountValues, serializeFilters } from "../utils/filters";
import RepositoryBase from "./repositoryBase";
import type BackOfficeConnector from "../data/backOfficeConnector";
import type EventBus from "../services/eventBus";
import RefundForRefundOfRefundResponse from "entities/refundForRefundOfRefundResponse";
import type { IPagination } from "data/types";
import type { IApprovalRepository } from "./types";
import type RefundType from "entities/refundType";
import type RefundForRefundOfRefundPayment from "entities/refundForRefundOfRefundPaymentResponse";
import type RefundsForRefundsOfRefundsRequest from "entities/refundsForRefundsOfRefundsRequest";
import RefundForRefundOfRefundDetailResponse from "entities/refundForRefundOfRefundDetailResponse";

export default class RefundForRefundOfRefundsRepository
  extends RepositoryBase<BackOfficeConnector>
  implements IApprovalRepository
{
  constructor(protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }

  search(pagination: IPagination, filter: RefundsForRefundsOfRefundsRequest) {
    return this.callApi(
      api =>
        api
          .all("finance")
          .all("refund_for_refund_of_refund_search")
          .postPagedEntities({ ...serializeFilters(filter, false), ...pagination }, RefundForRefundOfRefundResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "1.5" }),
      }
    );
  }

  get(refundId: string) {
    return this.callApi(
      api => api.all("finance").one("refunds_for_refunds_of_refunds", refundId).getEntity(RefundForRefundOfRefundDetailResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "1.5" }),
      }
    );
  }

  updateRefundPayment(refundId: string, refundPayment: RefundForRefundOfRefundPayment) {
    const { counterpartyBank, ...rest } = refundPayment;
    const request = { counterPartyBank: formatAccountValues(counterpartyBank), ...rest };
    if (request?.counterPartyBank?.bankAccount && !request?.counterPartyBank?.bankAccount.bank) {
      request.counterPartyBank.bankAccount.bank = undefined;
    }
    return this.callApi(api =>
      api
        .all("finance")
        .one("refunds_for_refunds_of_refunds", refundId)
        .all("refund_payment")
        .putOnly(excludeEmptyValues(request))
    );
  }

  approve(id: string) {
    return this.callApi(api =>
      api.all("finance").one("refunds_for_refunds_of_refunds", id).all("approvals").postWithoutResponse({})
    );
  }

  approveRefunds(refundType: RefundType) {
    return this.callApi(api =>
      api.all("finance").all("refunds_for_refunds_of_refunds").all("approvals").postWithoutResponse({
        refundType,
      })
    );
  }

  exportApprovedRefunds() {
    return this.callApi(api => api.all("finance").all("refunds_for_refunds_of_refunds").all("exports").postWithoutResponse({}));
  }
}
